import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link, useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { Sakura } from "../components/shrine/Sakura";
import { Miko } from "../components/miko/Miko";
import { useSound } from "../hooks/useSound";
import { useVibration, VibrationPatterns } from "../hooks/useVibration";
import {
  ensurePair,
  getOrCreateInvite,
  getProfile,
  saveProfile,
} from "../lib/storage";

/**
 * §1.2 初回ロック画面
 *
 * 「自分の結果すら見られない」絶望と「誰かに送る」動機を最大化。
 * 演出:
 *  - 巫女が告げる + 中央に光る封印された巻物
 *  - 「招待する」 → 巫女が札を取り出すアニメ → Web Share API シェアシート起動
 *  - クリップボードにもフォールバック (Share API 非対応端末)
 *  - 招待 URL: musubi-8g0.pages.dev/inv/<code>  (本番は musubi.gg/inv/<code>)
 *
 * Phase 1 では「招待した」ボタンで「ペア成立した体」で先に進める dev フローも提供
 * (Track B の招待受諾サーバが立つまでのつなぎ)。
 */

const INVITE_HOST = "musubi-8g0.pages.dev"; // staging cloudflare host (本番は musubi.gg)

type Step = "sealed" | "summoning" | "shared" | "waiting";

export function InitialLock() {
  const { t, i18n } = useTranslation();
  const [, navigate] = useLocation();
  const { unlock, play } = useSound();
  const { vibrate } = useVibration();

  const [step, setStep] = useState<Step>("sealed");
  const [copied, setCopied] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  // 既にロック解除済みなら飛ばす
  useEffect(() => {
    const p = getProfile();
    if (p?.isUnlocked) navigate("/home");
  }, [navigate]);

  const invite = useMemo(() => getOrCreateInvite(), []);
  const inviteUrl = `https://${INVITE_HOST}/inv/${invite.code}`;
  const profile = getProfile();
  const yourName =
    profile?.displayName ?? (i18n.language === "en" ? "Someone" : "誰か");

  const shareMessage =
    t("lock.shareMessage", { name: yourName }) + " " + inviteUrl;

  async function handleInvite() {
    unlock();
    play("scrollOpen");
    vibrate(VibrationPatterns.cardOpen);
    setShareError(null);
    setStep("summoning");

    // 巫女が札を取り出すアニメ (700ms 待ってからシェア起動)
    await new Promise((r) => setTimeout(r, 700));

    const sharePayload: ShareData = {
      title: "Musubi",
      text: t("lock.shareMessage", { name: yourName }),
      url: inviteUrl,
    };

    if (typeof navigator !== "undefined" && "share" in navigator) {
      try {
        await navigator.share(sharePayload);
        play("kirari");
        vibrate(VibrationPatterns.bellShake);
        setStep("shared");
      } catch (err) {
        // ユーザーキャンセルは無視
        if ((err as { name?: string })?.name !== "AbortError") {
          setShareError(String(err));
        }
        setStep("sealed");
      }
    } else {
      // Share API 非対応 → クリップボード
      try {
        await (navigator as Navigator).clipboard.writeText(shareMessage);
        setCopied(true);
        setStep("shared");
        play("kirari");
        vibrate(20);
      } catch (e) {
        setShareError(String(e));
        setStep("sealed");
      }
    }
  }

  function proceedAsAccepted() {
    // Phase 1: 招待を送った後、相手が受諾した「体」で先に進む dev フロー。
    // Track B の Cloud Functions が立ったら polling/notification に置換。
    play("taikoDon");
    vibrate(VibrationPatterns.daiKichi);

    // ペア確定 + 解除フラグ
    ensurePair({
      pairId: `inv_${invite.code}`,
      partnerName: i18n.language === "en" ? "Partner" : "招待した人",
      relationshipType: "friend",
    });
    saveProfile({ isUnlocked: true });

    // 1.3 へ
    navigate("/ritual/first");
  }

  return (
    <main className="relative min-h-dvh overflow-hidden bg-[var(--color-night-deep)] text-[var(--color-washi)]">
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at center 80%, rgba(159,29,53,0.18), transparent 60%), linear-gradient(to bottom, #050203 0%, #0a0608 50%, #1a0c12 100%)",
        }}
      />
      <Sakura count={10} />

      <Link
        href="/profile"
        className="absolute top-6 left-6 z-30 text-[10px] tracking-[0.3em] text-[var(--color-gold-light)] opacity-70"
      >
        ← {t("common.back")}
      </Link>

      <div className="relative z-10 min-h-dvh flex flex-col items-center justify-center px-6 py-12">
        {/* 巫女 */}
        <motion.div
          animate={{ y: [0, -6, 0] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        >
          <Miko
            size={170}
            className="drop-shadow-[0_8px_24px_rgba(159,29,53,0.5)]"
          />
        </motion.div>

        {/* セリフ */}
        <h1
          className="mt-6 text-base text-center max-w-sm leading-relaxed text-[var(--color-washi-warm)]"
          style={{
            fontFamily: "var(--font-fude)",
            textShadow: "0 2px 8px rgba(0,0,0,0.6)",
          }}
        >
          {t("lock.title")}
        </h1>
        <p className="mt-3 text-[11px] opacity-70 text-center max-w-sm leading-relaxed">
          {t("lock.subtitle")}
        </p>

        {/* 封印された巻物 (光る・ふわふわ) */}
        <motion.div
          aria-hidden
          className="mt-8 relative w-36 h-36 grid place-items-center"
          animate={{
            scale: [1, 1.04, 1],
            rotate: [0, 1.5, -1.5, 0],
          }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          {/* 光彩 */}
          <motion.span
            className="absolute inset-0 rounded-full"
            animate={{ opacity: [0.4, 0.8, 0.4] }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            style={{
              background:
                "radial-gradient(circle, rgba(201,169,97,0.5) 0%, rgba(159,29,53,0.25) 50%, transparent 80%)",
            }}
          />
          {/* 巻物本体 */}
          <span
            className="relative text-6xl"
            style={{
              fontFamily: "var(--font-fude)",
              color: "var(--color-gold-light)",
              textShadow:
                "0 0 24px rgba(201,169,97,0.7), 0 0 8px rgba(255,250,205,0.5)",
            }}
          >
            封
          </span>
        </motion.div>

        {/* CTA */}
        <div className="mt-10 w-full max-w-xs flex flex-col items-center gap-3">
          <motion.button
            onClick={handleInvite}
            whileTap={{ scale: 0.94 }}
            whileHover={{ scale: 1.03 }}
            disabled={step === "summoning"}
            className="relative w-full px-8 py-4 rounded-full text-[var(--color-washi)] tracking-[0.3em] text-sm disabled:opacity-60"
            style={{
              background:
                "linear-gradient(180deg, #c43a55 0%, #9f1d35 50%, #6b1024 100%)",
              border: "1px solid rgba(201,169,97,0.7)",
              boxShadow:
                "0 0 32px rgba(159,29,53,0.55), inset 0 1px 0 rgba(255,255,255,0.2)",
              fontFamily: "var(--font-fude)",
            }}
          >
            ✦ {t("lock.invite")} ✦
          </motion.button>

          <p className="text-[10px] tracking-widest opacity-50 text-center">
            {inviteUrl}
          </p>

          <AnimatePresence>
            {copied && (
              <motion.p
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                className="text-[10px] tracking-widest text-[var(--color-gold-light)]"
              >
                {i18n.language === "en"
                  ? "Link copied to clipboard"
                  : "リンクをコピーしました"}
              </motion.p>
            )}
          </AnimatePresence>

          {shareError && (
            <p className="text-[10px] text-red-300 opacity-80">
              {i18n.language === "en"
                ? "Could not open share sheet"
                : "シェアを開けませんでした"}
            </p>
          )}

          {/* シェア後 → ペア成立 (Phase 1 dev フロー) */}
          <AnimatePresence>
            {(step === "shared" || step === "waiting") && (
              <motion.button
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                onClick={proceedAsAccepted}
                className="mt-4 px-6 py-2.5 rounded-full text-[11px] tracking-[0.3em]"
                style={{
                  background: "rgba(10,6,8,0.7)",
                  border: "1px solid rgba(201,169,97,0.6)",
                  color: "var(--color-gold-light)",
                }}
              >
                {i18n.language === "en"
                  ? "✦ Partner accepted ✦"
                  : "✦ 相手が受け取った ✦"}
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* 巫女が札を取り出すアニメ */}
        <AnimatePresence>
          {step === "summoning" && (
            <motion.div
              key="summoning"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none"
            >
              <motion.div
                initial={{ scale: 0.4, rotate: -20, opacity: 0 }}
                animate={{ scale: 1.1, rotate: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="w-24 h-32 rounded-md flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, #fffacd, #c9a961, #fffacd)",
                  boxShadow: "0 0 36px rgba(201,169,97,0.85)",
                }}
              >
                <span
                  className="text-3xl"
                  style={{ fontFamily: "var(--font-fude)", color: "#6b1024" }}
                >
                  招
                </span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </main>
  );
}
